import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Row,
    Space,
    Spin,
    Typography,
    message,
} from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useIntl } from "react-intl";
import {
    useGetBrandKnowledgeQuery,
    useUpdateBrandKnowledgeMutation,
    useSuggestFieldsFromDocumentMutation,
    useSuggestFieldsFromKnowledgeMutation,
} from "../../services/nestjs-base-api";
import {
    IUpdateBrandKnowledge,
    IBrandFieldSuggestions,
} from "../../services/nestjs-base-api";
import BrandKnowledgeDocuments from "./BrandKnowledgeDocuments";
import FieldSuggestionsModal from "./FieldSuggestionsModal";
import { BulbOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface IProps {
    brandId?: number;
}

interface IFieldSpec {
    name: keyof IUpdateBrandKnowledge;
    labelId: string;
    labelDefault: string;
    helpId: string;
    helpDefault: string;
    rows: number;
}

// Guided fields in the order they render (Additional Context last).
const FIELDS: IFieldSpec[] = [
    {
        name: "positioning",
        labelId: "brandKnowledge.field.positioning.label",
        labelDefault: "Positioning",
        helpId: "brandKnowledge.field.positioning.help",
        helpDefault: "How your brand positions itself in the market.",
        rows: 3,
    },
    {
        name: "productsServices",
        labelId: "brandKnowledge.field.productsServices.label",
        labelDefault: "Products & Services",
        helpId: "brandKnowledge.field.productsServices.help",
        helpDefault: "What your brand sells and what makes it distinctive.",
        rows: 3,
    },
    {
        name: "targetCustomers",
        labelId: "brandKnowledge.field.targetCustomers.label",
        labelDefault: "Target Customers",
        helpId: "brandKnowledge.field.targetCustomers.help",
        helpDefault: "Who you serve: key segments and customer personas.",
        rows: 3,
    },
    {
        name: "keyCompetitors",
        labelId: "brandKnowledge.field.keyCompetitors.label",
        labelDefault: "Key Competitors",
        helpId: "brandKnowledge.field.keyCompetitors.help",
        helpDefault: "Main competitors and how you differentiate from them.",
        rows: 3,
    },
    {
        name: "toneOfVoice",
        labelId: "brandKnowledge.field.toneOfVoice.label",
        labelDefault: "Tone of Voice",
        helpId: "brandKnowledge.field.toneOfVoice.help",
        helpDefault: "How your brand sounds: voice and tone guidelines.",
        rows: 3,
    },
    {
        name: "brandGoals",
        labelId: "brandKnowledge.field.brandGoals.label",
        labelDefault: "Brand Goals",
        helpId: "brandKnowledge.field.brandGoals.help",
        helpDefault: "Strategic goals and objectives for the brand.",
        rows: 3,
    },
    {
        name: "additionalContext",
        labelId: "brandKnowledge.field.additionalContext.label",
        labelDefault: "Anything Else",
        helpId: "brandKnowledge.field.additionalContext.help",
        helpDefault:
            "Any other context you'd like the AI to know about your brand.",
        rows: 5,
    },
];

/**
 * SYR-78 — the "Brand Knowledge" tab under Organisation settings. Guided fields +
 * freeform context that ground the AI Experts and other AI subsystems.
 */
const BrandKnowledge: React.FunctionComponent<IProps> = ({ brandId }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [form] = Form.useForm();

    const { data, isLoading, isFetching } = useGetBrandKnowledgeQuery(
        { jwtToken: user?.jwtToken || "", brandId: brandId as number },
        { skip: !user?.jwtToken || !brandId }
    );

    const [updateBrandKnowledge, { isLoading: isSaving }] =
        useUpdateBrandKnowledgeMutation();
    const [suggestFromDoc] = useSuggestFieldsFromDocumentMutation();
    const [suggestFromKnowledge, { isLoading: isSuggesting }] =
        useSuggestFieldsFromKnowledgeMutation();
    const [suggestions, setSuggestions] =
        useState<IBrandFieldSuggestions | null>(null);
    // Tracks unsaved user edits so Save is only highlighted when there's something
    // to save. Programmatic setFieldsValue (initial load, applying suggestions)
    // does NOT fire onValuesChange, so it won't mark the form dirty.
    const [dirty, setDirty] = useState(false);

    const openSuggestions = (result: IBrandFieldSuggestions) => {
        if (result && Object.keys(result).length > 0) {
            setSuggestions(result);
        } else {
            message.info(
                intl.formatMessage({
                    id: "brandKnowledge.suggest.none",
                    defaultMessage:
                        "No new field suggestions found in the current content.",
                })
            );
        }
    };

    // Called by the documents panel after a doc/scrape is ingested.
    const handleIngested = async (documentId: number) => {
        if (!brandId || !user?.jwtToken) return;
        try {
            const result = await suggestFromDoc({
                jwtToken: user.jwtToken,
                brandId,
                documentId,
            }).unwrap();
            openSuggestions(result);
        } catch {
            /* suggestion failures are non-blocking */
        }
    };

    const handleManualSuggest = async () => {
        if (!brandId || !user?.jwtToken) return;
        try {
            const result = await suggestFromKnowledge({
                jwtToken: user.jwtToken,
                brandId,
            }).unwrap();
            openSuggestions(result);
        } catch (err: any) {
            message.error(
                err?.data?.message ||
                    intl.formatMessage({
                        id: "brandKnowledge.suggest.error",
                        defaultMessage: "Could not generate suggestions",
                    })
            );
        }
    };

    const handleApplySuggestions = async (
        accepted: Partial<Record<string, string>>
    ) => {
        if (!brandId || !user?.jwtToken) return;
        form.setFieldsValue(accepted);
        try {
            await updateBrandKnowledge({
                jwtToken: user.jwtToken,
                brandId,
                payload: form.getFieldsValue(),
            }).unwrap();
            message.success(
                intl.formatMessage({
                    id: "brandKnowledge.suggest.applied",
                    defaultMessage: "Brand context updated",
                })
            );
        } catch {
            message.error(
                intl.formatMessage({
                    id: "brandKnowledge.saveError",
                    defaultMessage: "Could not save brand context",
                })
            );
        }
    };

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                positioning: data.positioning ?? "",
                productsServices: data.productsServices ?? "",
                targetCustomers: data.targetCustomers ?? "",
                keyCompetitors: data.keyCompetitors ?? "",
                toneOfVoice: data.toneOfVoice ?? "",
                brandGoals: data.brandGoals ?? "",
                additionalContext: data.additionalContext ?? "",
            });
        }
    }, [data, form]);

    const handleSave = async (values: IUpdateBrandKnowledge) => {
        if (!brandId || !user?.jwtToken) return;
        try {
            await updateBrandKnowledge({
                jwtToken: user.jwtToken,
                brandId,
                payload: values,
            }).unwrap();
            setDirty(false);
            message.success(
                intl.formatMessage({
                    id: "brandKnowledge.saved",
                    defaultMessage: "Brand context saved",
                })
            );
        } catch (err) {
            message.error(
                intl.formatMessage({
                    id: "brandKnowledge.saveError",
                    defaultMessage: "Could not save brand context",
                })
            );
        }
    };

    if (!brandId) {
        return (
            <Text type="secondary">
                {intl.formatMessage({
                    id: "brandKnowledge.noBrand",
                    defaultMessage:
                        "Create a brand first to add brand context for the AI.",
                })}
            </Text>
        );
    }

    if (isLoading || isFetching) {
        return (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Spin />
            </div>
        );
    }

    return (
        <Card>
            <Title level={4} style={{ marginTop: 0 }}>
                {intl.formatMessage({
                    id: "brandKnowledge.title",
                    defaultMessage: "Brand Knowledge",
                })}
            </Title>
            <Paragraph type="secondary">
                {intl.formatMessage({
                    id: "brandKnowledge.description",
                    defaultMessage:
                        "Tell the AI about your brand so its recommendations are tailored to you. This context grounds the AI Experts and other AI features. Leave a field blank if it doesn't apply.",
                })}
            </Paragraph>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                onValuesChange={() => setDirty(true)}
                requiredMark={false}
            >
                <Row gutter={16}>
                    {FIELDS.map((field) => {
                        const isFull = field.name === "additionalContext";
                        return (
                            <Col xs={24} md={isFull ? 24 : 12} key={field.name}>
                                <Form.Item
                                    name={field.name}
                                    label={
                                        <span>
                                            {intl.formatMessage({
                                                id: field.labelId,
                                                defaultMessage: field.labelDefault,
                                            })}
                                            <Text
                                                type="secondary"
                                                style={{
                                                    fontWeight: 400,
                                                    fontSize: 12,
                                                    marginLeft: 6,
                                                }}
                                            >
                                                ·{" "}
                                                {intl.formatMessage({
                                                    id: field.helpId,
                                                    defaultMessage:
                                                        field.helpDefault,
                                                })}
                                            </Text>
                                        </span>
                                    }
                                >
                                    <TextArea
                                        // Uniform fixed height for every field;
                                        // longer content scrolls inside the box.
                                        autoSize={{ minRows: 5, maxRows: 5 }}
                                    />
                                </Form.Item>
                            </Col>
                        );
                    })}
                </Row>
                <Space>
                    <Button
                        type={dirty ? "primary" : "default"}
                        htmlType="submit"
                        loading={isSaving}
                        disabled={!dirty}
                    >
                        {intl.formatMessage({
                            id: "brandKnowledge.save",
                            defaultMessage: "Save",
                        })}
                    </Button>
                    <Button
                        icon={<BulbOutlined />}
                        loading={isSuggesting}
                        onClick={handleManualSuggest}
                    >
                        {intl.formatMessage({
                            id: "brandKnowledge.suggest.button",
                            defaultMessage: "Suggest field content",
                        })}
                    </Button>
                </Space>
            </Form>
            <BrandKnowledgeDocuments
                brandId={brandId}
                onIngested={handleIngested}
            />
            <FieldSuggestionsModal
                open={!!suggestions}
                suggestions={suggestions || {}}
                currentValues={{
                    positioning: form.getFieldValue("positioning") || "",
                    productsServices:
                        form.getFieldValue("productsServices") || "",
                    targetCustomers: form.getFieldValue("targetCustomers") || "",
                    keyCompetitors: form.getFieldValue("keyCompetitors") || "",
                    toneOfVoice: form.getFieldValue("toneOfVoice") || "",
                    brandGoals: form.getFieldValue("brandGoals") || "",
                }}
                onApply={handleApplySuggestions}
                onClose={() => setSuggestions(null)}
            />
        </Card>
    );
};

export default BrandKnowledge;
